// Bulk unit import from a spreadsheet (CSV). The admin downloads a template,
// fills one unit per row, and uploads it back. Pure functions here: parsing,
// checking each row against the rules and the units that already exist. The
// dialog turns the valid rows into createUnit() calls.

import type { AdminUnit, CreateAdminUnitRequest } from '@residenone/shared';
import { csvFile } from './annual-export';
import { headerKey, parseCsv } from './csv-import';

export type ImportUnitType = NonNullable<CreateAdminUnitRequest['type']>;
export type ImportOccupancy = 'OCCUPIED' | 'VACANT';

export interface ImportRow {
  /** Line in the file, counting the header as line 1, for "row 7: …" messages. */
  line: number;
  block: string;
  number: string;
  label: string;
  type: ImportUnitType;
  occupancy: ImportOccupancy;
  /** An i18n key and its values when the row can't be imported. */
  error?: { key: string; vars?: Record<string, string | number> };
}

export type ImportParse =
  | { ok: true; rows: ImportRow[] }
  | { ok: false; error: 'empty' | 'noNumberColumn' };

type ImportRowColumn = 'block' | 'number' | 'label' | 'type' | 'occupancy';

// Column headers as they appear in the template (Indonesian), with the English
// ones accepted too, matched without case or spaces.
const COLUMNS: Record<string, ImportRowColumn> = {
  blok: 'block',
  blokjalan: 'block',
  block: 'block',
  nomor: 'number',
  no: 'number',
  number: 'number',
  label: 'label',
  tipe: 'type',
  type: 'type',
  hunian: 'occupancy',
  occupancy: 'occupancy',
};

const TYPES: Record<string, ImportUnitType> = {
  rumah: 'HOUSE',
  house: 'HOUSE',
  apartemen: 'APARTMENT',
  apartment: 'APARTMENT',
  ruko: 'RUKO',
};

const OCCUPANCIES: Record<string, ImportOccupancy> = {
  dihuni: 'OCCUPIED',
  occupied: 'OCCUPIED',
  kosong: 'VACANT',
  vacant: 'VACANT',
};

// The same limits as the API (createAdminUnitRequest), so a row that passes
// here isn't refused there for its length.
const MAX_PART = 20;
const MAX_LABEL = 60;

/** The template: the header and two example rows showing both styles of address. */
export function unitTemplateCsv(): string {
  return csvFile([
    ['Blok / Jalan', 'Nomor', 'Label', 'Tipe', 'Hunian'],
    ['A2', '18', '', 'Rumah', 'Dihuni'],
    ['Matahari', '5', 'Ruko Matahari 5', 'Ruko', 'Kosong'],
  ]);
}

/** Block + number as one comparable key: "a2|18". */
const unitKey = (block: string, number: string) => `${block.trim().toLowerCase()}|${number.trim().toLowerCase()}`;

/** Reads an import file and checks every row; rows with problems carry an error. */
export function parseUnitImport(text: string, existing: AdminUnit[]): ImportParse {
  const [head, ...body] = parseCsv(text);
  const isBlank = (r: string[]) => r.every((c) => c.trim() === '');
  if (!head || body.every(isBlank)) return { ok: false, error: 'empty' };

  const header = head.map((h) => COLUMNS[headerKey(h)]);
  if (!header.includes('number')) return { ok: false, error: 'noNumberColumn' };

  const taken = new Set(existing.map((u) => unitKey(u.block, u.number)));
  // A cluster's units either all have a block or none do, so follow the ones
  // already there (the server refuses a missing block where blocks are used).
  const needsBlock = existing.some((u) => u.block.trim() !== '');
  const seen = new Map<string, number>();
  const rows: ImportRow[] = [];

  for (const [i, raw] of body.entries()) {
    if (isBlank(raw)) continue;
    // Line numbers follow the file (header is line 1), so blank lines don't shift them.
    const line = i + 2;
    const get = (col: ImportRowColumn) => {
      const at = header.indexOf(col);
      return at >= 0 ? (raw[at] ?? '').trim() : '';
    };

    const block = get('block');
    const number = get('number');
    const label = get('label');
    const typeText = get('type');
    const occText = get('occupancy');
    const type = typeText ? TYPES[headerKey(typeText)] : 'HOUSE';
    const occupancy = occText ? OCCUPANCIES[headerKey(occText)] : 'OCCUPIED';

    const row: ImportRow = { line, block, number, label, type: type ?? 'HOUSE', occupancy: occupancy ?? 'OCCUPIED' };
    const key = unitKey(block, number);
    if (!number) row.error = { key: 'units.import.errNumber' };
    else if (needsBlock && !block) row.error = { key: 'units.import.errBlock' };
    else if (block.length > MAX_PART || number.length > MAX_PART) row.error = { key: 'units.import.errTooLong', vars: { max: MAX_PART } };
    else if (label.length > MAX_LABEL) row.error = { key: 'units.import.errLabel', vars: { max: MAX_LABEL } };
    else if (!type) row.error = { key: 'units.import.errType', vars: { value: typeText } };
    else if (!occupancy) row.error = { key: 'units.import.errOccupancy', vars: { value: occText } };
    else if (taken.has(key)) row.error = { key: 'units.import.errExists' };
    else if (seen.has(key)) row.error = { key: 'units.import.errDuplicate', vars: { line: seen.get(key)! } };

    if (!row.error) seen.set(key, line);
    rows.push(row);
  }
  return { ok: true, rows };
}

/** The request body for one valid row. */
export function importRequest(row: ImportRow): CreateAdminUnitRequest {
  return {
    block: row.block,
    number: row.number,
    ...(row.label ? { label: row.label } : {}),
    type: row.type,
    occupancy: row.occupancy,
  };
}
